import { useCallback, useEffect, useRef, useState } from "react";
import { ProvetContextType } from "../../../context/provetContext";
import { Breed, FormSelectItem, OnlineBookingPatient, Species } from "../../../types";
import { filterSpeciesByReasonCodes } from "../helpers/contactDetailsUtility";

export interface UseSpeciesAndBreedsProps {
  provetContext: ProvetContextType;
  defaultSpecies?: FormSelectItem[];
  patientInfo?: OnlineBookingPatient;
  onSpeciesAutoSelected?: (species: Species) => void;
}

export function useSpeciesAndBreeds({
  provetContext,
  defaultSpecies,
  patientInfo,
  onSpeciesAutoSelected
}: UseSpeciesAndBreedsProps) {
  const [species, setSpecies] = useState<Species[]>([]);
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [speciesItems, setSpeciesItems] = useState<FormSelectItem[]>([]);
  const [breedItems, setBreedItems] = useState<FormSelectItem[]>([]);
  const [selectedSpecies, setSelectedSpecies] = useState<Species | undefined>(undefined);
  const [selectedBreed, setSelectedBreed] = useState<Breed | undefined>(undefined);
  const [selectedSpeciesId, setSelectedSpeciesId] = useState<number>(0);
  const [selectedBreedCode, setSelectedBreedCode] = useState<number>(-1);

  // Use ref to track if we've already notified about auto-selection
  const hasNotifiedAutoSelection = useRef<number | undefined>(undefined);
  // Use ref for callback to avoid adding it to dependencies
  const onSpeciesAutoSelectedRef = useRef(onSpeciesAutoSelected);

  // Keep ref updated
  useEffect(() => {
    onSpeciesAutoSelectedRef.current = onSpeciesAutoSelected;
  }, [onSpeciesAutoSelected]);

  // Fetch and initialize data
  useEffect(() => {
    const initializeData = async () => {
      try {
        // The fetches reject on failure (and log it). Each falls back to an empty list on
        // its own, so a breeds failure doesn't also hide the species.
        const fetchedSpecies = await provetContext.api
          .fetchSpecies()
          .catch((): Species[] => []);
        const fetchedBreeds = await provetContext.api
          .fetchBreeds()
          .catch((): Breed[] => []);

        if (fetchedSpecies && fetchedSpecies.length > 0) {
          setSpecies(fetchedSpecies);
          const currentReasonSpeciesCodes =
            provetContext.bookingFlowState.formData?.selectedReason?.species_codes;
          const filteredSpeciesItems = filterSpeciesByReasonCodes(
            fetchedSpecies,
            currentReasonSpeciesCodes,
            defaultSpecies
          );
          setSpeciesItems(filteredSpeciesItems);

          if (filteredSpeciesItems.length === 1) {
            const singleSpeciesItem = filteredSpeciesItems[0];
            const speciesId = parseInt(singleSpeciesItem.value);
            const foundSpecies = fetchedSpecies.find(s => s.id === speciesId);
            if (foundSpecies) {
              setSelectedSpecies(foundSpecies);
              setSelectedSpeciesId(speciesId);

              // Notify parent component that species was auto-selected (only once per species)
              if (
                onSpeciesAutoSelectedRef.current &&
                hasNotifiedAutoSelection.current !== speciesId
              ) {
                hasNotifiedAutoSelection.current = speciesId;
                onSpeciesAutoSelectedRef.current(foundSpecies);
              }

              if (fetchedBreeds && fetchedBreeds.length > 0) {
                const filteredBreeds = fetchedBreeds.filter(
                  breed =>
                    breed &&
                    breed.parent_id === speciesId &&
                    breed.label &&
                    breed.code !== undefined
                );
                setBreedItems(
                  filteredBreeds
                    .map(breed => ({
                      caption: breed.label || "",
                      value: breed.code?.toString() || ""
                    }))
                    .filter(item => item.value !== "")
                );
              }
            }
          }
        }

        if (fetchedBreeds && fetchedBreeds.length > 0) {
          setBreeds(fetchedBreeds);
        }

        // Set initial patient info if provided
        if (patientInfo) {
          if (patientInfo.species) {
            setSelectedSpecies(patientInfo.species);
            setSelectedSpeciesId(patientInfo.species.id);
          }
          if (patientInfo.breed) {
            setSelectedBreed(patientInfo.breed);
            setSelectedBreedCode(patientInfo.breed.code);
          }
        }
      } catch (error) {
        setSpecies([]);
        setBreeds([]);
        setSpeciesItems([]);
        setBreedItems([]);
      }
    };
    initializeData();
  }, [
    provetContext.api,
    provetContext.bookingFlowState.formData?.selectedReason,
    defaultSpecies,
    patientInfo
  ]);

  // Effect for reason species codes changes
  useEffect(() => {
    if (species.length > 0) {
      const currentReasonSpeciesCodes =
        provetContext.bookingFlowState.formData?.selectedReason?.species_codes;
      const filteredSpeciesItems = filterSpeciesByReasonCodes(
        species,
        currentReasonSpeciesCodes,
        defaultSpecies
      );
      setSpeciesItems(filteredSpeciesItems);
      // Only update selectedSpecies if it is not already set
      if (!selectedSpecies && !patientInfo?.species) {
        if (filteredSpeciesItems.length === 1) {
          const singleSpeciesItem = filteredSpeciesItems[0];
          const speciesId = parseInt(singleSpeciesItem.value);
          const foundSpecies = species.find(s => s.id === speciesId);
          if (foundSpecies) {
            setSelectedSpecies(foundSpecies);
            setSelectedSpeciesId(speciesId);

            // Notify parent component that species was auto-selected (only once per species)
            if (
              onSpeciesAutoSelectedRef.current &&
              hasNotifiedAutoSelection.current !== speciesId
            ) {
              hasNotifiedAutoSelection.current = speciesId;
              onSpeciesAutoSelectedRef.current(foundSpecies);
            }

            if (breeds.length > 0) {
              const filteredBreeds = breeds.filter(
                breed =>
                  breed &&
                  breed.parent_id === speciesId &&
                  breed.label &&
                  breed.code !== undefined
              );
              setBreedItems(
                filteredBreeds
                  .map(breed => ({
                    caption: breed.label || "",
                    value: breed.code?.toString() || ""
                  }))
                  .filter(item => item.value !== "")
              );
            }
          }
        } else if (filteredSpeciesItems.length !== 1) {
          setSelectedSpecies(undefined);
          setSelectedSpeciesId(0);
          setBreedItems([]);
          setSelectedBreedCode(-1);
          // Reset the notification tracker when species is cleared
          hasNotifiedAutoSelection.current = undefined;
        }
      }
    }
  }, [
    provetContext.bookingFlowState.formData?.selectedReason?.species_codes,
    species,
    breeds,
    defaultSpecies,
    selectedSpecies,
    patientInfo?.species
  ]);

  // Effect to populate breed items when breeds load after species selection
  useEffect(() => {
    // If a species is selected but breedItems is empty, and breeds have loaded
    if (selectedSpeciesId > 0 && breedItems.length === 0 && breeds.length > 0) {
      const filteredBreeds = breeds.filter(
        breed =>
          breed &&
          breed.parent_id === selectedSpeciesId &&
          breed.label &&
          breed.code !== undefined
      );

      setBreedItems(
        filteredBreeds
          .map(breed => ({
            caption: breed.label || "",
            value: breed.code?.toString() || ""
          }))
          .filter(item => item.value !== "")
      );
    }
  }, [selectedSpeciesId, breeds, breedItems.length]);

  // Handlers
  const onSpeciesSearch = useCallback(
    (searchValue: string) => {
      if (!species || !searchValue.trim()) {
        const currentReasonSpeciesCodes =
          provetContext.bookingFlowState.formData?.selectedReason?.species_codes;
        const filteredSpeciesItems = filterSpeciesByReasonCodes(
          species,
          currentReasonSpeciesCodes,
          defaultSpecies
        );
        setSpeciesItems(filteredSpeciesItems);
        return;
      }
      const currentReasonSpeciesCodes =
        provetContext.bookingFlowState.formData?.selectedReason?.species_codes;
      let baseSpecies = species;
      if (currentReasonSpeciesCodes && currentReasonSpeciesCodes.length > 0) {
        baseSpecies = species.filter(
          s => s?.code !== undefined && currentReasonSpeciesCodes.includes(s.code)
        );
      }
      const searchFilteredSpecies = baseSpecies.filter(
        spec => spec.label && spec.label.toLowerCase().includes(searchValue.toLowerCase())
      );
      const searchResults = searchFilteredSpecies.map(speciesItem => ({
        caption: speciesItem.label || "",
        value: speciesItem.id.toString()
      }));
      setSpeciesItems(searchResults);
      setBreedItems([]);
      setSelectedBreedCode(-1);
    },
    [
      species,
      provetContext.bookingFlowState.formData?.selectedReason?.species_codes,
      defaultSpecies
    ]
  );

  const onSpeciesSelected = useCallback(
    (item: FormSelectItem) => {
      if (!item || !item.value) {
        setSelectedSpecies(undefined);
        setSelectedSpeciesId(0);
        setBreedItems([]);
        setSelectedBreedCode(-1);
        return;
      }

      const speciesId = parseInt(item.value);
      if (speciesId === 0 || isNaN(speciesId)) {
        return;
      }

      const foundSpecies = species.find(s => s.id === speciesId);
      setSelectedSpecies(foundSpecies || undefined);
      setSelectedSpeciesId(speciesId);

      if (foundSpecies && breeds && breeds.length > 0) {
        const filteredBreeds = breeds.filter(
          breed =>
            breed &&
            breed.parent_id === speciesId &&
            breed.label &&
            breed.code !== undefined
        );

        setBreedItems(
          filteredBreeds
            .map(breed => ({
              caption: breed.label || "",
              value: breed.code?.toString() || ""
            }))
            .filter(item => item.value !== "")
        );
        setSelectedBreedCode(-1);
      }
    },
    [
      species,
      breeds,
      setSelectedSpecies,
      setSelectedSpeciesId,
      setBreedItems,
      setSelectedBreedCode
    ]
  );

  const onBreedSearch = useCallback(
    (searchValue: string) => {
      if (!selectedSpecies || !searchValue.trim() || !breeds || breeds.length === 0) {
        return;
      }
      const filteredBreeds = breeds.filter(
        breed =>
          breed &&
          breed.parent_id === selectedSpeciesId &&
          breed.label &&
          breed.label.toLowerCase().includes(searchValue.toLowerCase()) &&
          breed.code !== undefined
      );
      setBreedItems(
        filteredBreeds
          .map(breed => ({
            caption: breed.label || "",
            value: breed.code?.toString() || ""
          }))
          .filter(item => item.value !== "")
      );
      setSelectedBreedCode(-1);
    },
    [breeds, selectedSpecies, selectedSpeciesId]
  );

  const onBreedSelected = useCallback(
    (item: FormSelectItem) => {
      const breedCode = parseInt(item?.value || "0");
      setSelectedBreedCode(breedCode);
      const foundBreed = breeds.find(breed => breed.code === breedCode);
      setSelectedBreed(foundBreed || undefined);
    },
    [breeds]
  );

  return {
    species,
    breeds,
    speciesItems,
    breedItems,
    selectedSpecies,
    selectedBreed,
    selectedSpeciesId,
    selectedBreedCode,
    setSelectedSpecies,
    setSelectedBreed,
    setSelectedSpeciesId,
    setSelectedBreedCode,
    onSpeciesSearch,
    onSpeciesSelected,
    onBreedSearch,
    onBreedSelected
  };
}
